import React, { useCallback, useEffect, useState } from 'react';
import { Button, Drawer, Form, Input, InputNumber, Popconfirm, Select, Space, Switch, Table, Tag, Typography, message } from 'antd';
import { BellOutlined, PlusOutlined, ReloadOutlined } from '@ant-design/icons';
import PageHeader from '../../../shared/components/PageHeader';
import supportAdminService from '../../services/supportAdminService';
import { useTranslation } from '../../../shared/i18n';
import { ok } from './common';
import I18nFieldTabs, { parseI18n, cleanI18n } from '../../components/I18nFieldTabs';

const { Text } = Typography;

const Departments = () => {
    const { t } = useTranslation();
    const [form] = Form.useForm();
    const [rows, setRows] = useState([]);
    const [admins, setAdmins] = useState([]);
    const [loading, setLoading] = useState(false);
    const [editing, setEditing] = useState(null); // null=关闭, {}=新建, row=编辑
    const [members, setMembers] = useState([]); // [{admin_id, notify}]
    const [busy, setBusy] = useState('');

    const load = useCallback(async () => {
        setLoading(true);
        const r = await supportAdminService.getDepartments();
        setLoading(false);
        if (ok(r)) setRows(Array.isArray(r.data) ? r.data : []);
    }, []);
    useEffect(() => {
        load();
        supportAdminService.getMeta().then((r) => { if (ok(r)) setAdmins(r.data?.admins || []); });
    }, [load]);

    const open = (row) => {
        const r = row || {};
        setEditing(r);
        setMembers((r.members || []).map((m) => ({ admin_id: Number(m.admin_id), notify: !!m.notify })));
        form.setFieldsValue({
            key: r.key || '', name: r.name || '', description: r.description || '',
            public: r.id ? !!Number(r.public) : true, active: r.id ? r.status !== 'disabled' : true,
            sort: r.sort ?? 0, email_to: r.email_to || '', telegram_chat_id: r.telegram_chat_id || '',
            i18n: parseI18n(r.i18n),
        });
    };
    const close = () => { setEditing(null); form.resetFields(); };

    const adminLabel = (id) => {
        const a = admins.find((x) => Number(x.id) === Number(id));
        return a ? (a.username || a.email) : `#${id}`;
    };

    const save = async () => {
        const v = await form.validateFields();
        setBusy('save');
        const r = await supportAdminService.saveDepartment({
            id: editing?.id || undefined,
            key: v.key.trim(), name: v.name.trim(), description: v.description || '',
            email_to: v.email_to || '', telegram_chat_id: (v.telegram_chat_id || '').trim(),
            public: v.public ? 1 : 0, status: v.active ? 'active' : 'disabled', sort: v.sort || 0,
            members, i18n: cleanI18n(v.i18n),
        });
        setBusy('');
        if (ok(r)) { message.success(t('Saved')); close(); load(); } else message.error(r?.msg || t('Failed to save'));
    };

    const test = async () => {
        setBusy('test');
        const r = await supportAdminService.testDepartment(editing.id);
        setBusy('');
        if (!ok(r)) { message.error(r?.msg || t('Send failed')); return; }
        const res = Object.entries(r.data || {});
        if (!res.length) { message.warning(t('No notification channels available')); return; }
        const allOk = res.every(([, x]) => x);
        message[allOk ? 'success' : 'warning'](res.map(([k, x]) => `${k} ${x ? '✓' : '✗'}`).join('  ·  '), 6);
    };

    const remove = async () => {
        setBusy('delete');
        const r = await supportAdminService.deleteDepartment(editing.id);
        setBusy('');
        if (ok(r)) { message.success(t('Deleted')); close(); load(); } else message.error(r?.msg || t('Deletion failed'));
    };

    const columns = [
        {
            title: t('Department'), dataIndex: 'name',
            render: (v, r) => <Space direction="vertical" size={0}><Text strong>{v}</Text><Text type="secondary" style={{ fontSize: 12 }}>{r.key}</Text></Space>,
        },
        { title: t('Description'), dataIndex: 'description', ellipsis: true, render: (v) => v || '-' },
        {
            title: t('Members'), dataIndex: 'members',
            render: (ms) => (ms && ms.length ? (
                <Space size={[4, 4]} wrap>
                    {ms.map((m) => <Tag key={m.admin_id} icon={m.notify ? <BellOutlined /> : null}>{m.username || m.email}</Tag>)}
                </Space>
            ) : <Text type="secondary">-</Text>),
        },
        { title: t('Pending'), dataIndex: 'open_tickets', width: 90, render: (v) => (v ? <Tag color="orange">{v}</Tag> : 0) },
        { title: t('Selectable by users'), dataIndex: 'public', width: 90, render: (v) => (Number(v) ? <Tag color="green">{t('Yes')}</Tag> : <Tag>{t('No')}</Tag>) },
        { title: t('Status'), dataIndex: 'status', width: 80, render: (v) => (v === 'disabled' ? <Tag>{t('Disable')}</Tag> : <Tag color="green">{t('Enable')}</Tag>) },
        { title: t('Sort'), dataIndex: 'sort', width: 70 },
        { title: '', key: 'op', width: 70, render: (_, r) => <Button type="link" size="small" onClick={() => open(r)}>{t('Edit')}</Button> },
    ];

    return (
        <div>
            <PageHeader title={t('Ticket departments')} extra={<Space>
                <Button icon={<ReloadOutlined />} onClick={load} loading={loading} />
                <Button type="primary" icon={<PlusOutlined />} onClick={() => open(null)}>{t('New department')}</Button>
            </Space>} />
            <Table size="middle" rowKey="id" loading={loading} dataSource={rows} columns={columns} pagination={false} />

            <Drawer open={!!editing} onClose={close} width={520} forceRender
                title={editing?.id ? t('Edit department') : t('New department')}
                extra={<Space>
                    {editing?.id && (
                        <Popconfirm title={t('Delete this department?')} onConfirm={remove}>
                            <Button danger loading={busy === 'delete'}>{t('Delete')}</Button>
                        </Popconfirm>
                    )}
                    {editing?.id && <Button loading={busy === 'test'} onClick={test}>{t('Send test')}</Button>}
                    <Button type="primary" loading={busy === 'save'} onClick={save}>{t('Save')}</Button>
                </Space>}>
                <Form form={form} layout="vertical">
                    <Form.Item name="key" label={t('Key')} style={{ width: 160 }}
                        rules={[{ required: true, pattern: /^[a-z][a-z0-9_-]{1,31}$/, message: t('Must start with a lowercase letter') }]}>
                        <Input placeholder="billing" />
                    </Form.Item>
                    <I18nFieldTabs
                        fields={[{ key: 'name', label: t('Name') }, { key: 'description', label: t('Description') }]}
                        base={<>
                            <Form.Item name="name" label={t('Name')} rules={[{ required: true, whitespace: true }]}>
                                <Input maxLength={64} />
                            </Form.Item>
                            <Form.Item name="description" label={t('Description')}>
                                <Input maxLength={255} />
                            </Form.Item>
                        </>}
                    />
                    <Space size={24}>
                        <Form.Item name="public" label={t('Selectable by users')} valuePropName="checked"><Switch /></Form.Item>
                        <Form.Item name="active" label={t('Enable')} valuePropName="checked"><Switch /></Form.Item>
                        <Form.Item name="sort" label={t('Sort')}><InputNumber style={{ width: 90 }} /></Form.Item>
                    </Space>
                    <Form.Item label={t('Members')}>
                        <Select mode="multiple" optionFilterProp="label" value={members.map((m) => m.admin_id)}
                            onChange={(ids) => setMembers(ids.map((aid) => members.find((m) => m.admin_id === aid) || { admin_id: aid, notify: true }))}
                            options={admins.map((a) => ({ value: Number(a.id), label: a.username || a.email }))} />
                        {members.length > 0 && (
                            <Space direction="vertical" size={4} style={{ width: '100%', marginTop: 8 }}>
                                {members.map((m) => (
                                    <Space key={m.admin_id} style={{ width: '100%', justifyContent: 'space-between' }}>
                                        <Text>{adminLabel(m.admin_id)}</Text>
                                        <Space size={6}>
                                            <Text type="secondary" style={{ fontSize: 12 }}>{t('Notifications')}</Text>
                                            <Switch size="small" checked={m.notify}
                                                onChange={(c) => setMembers(members.map((x) => (x.admin_id === m.admin_id ? { ...x, notify: c } : x)))} />
                                        </Space>
                                    </Space>
                                ))}
                            </Space>
                        )}
                    </Form.Item>
                    <Form.Item name="email_to" label={t('Notification emails')} tooltip={t('Separate multiple with commas')}>
                        <Input placeholder="support@example.com, ops@example.com" />
                    </Form.Item>
                    <Form.Item name="telegram_chat_id" label={t('Telegram group chat_id')}
                        extra={t('Group chat_id (the bot must be in the group); leave empty to use the ops alert group')}>
                        <Input placeholder="-1001234567890" />
                    </Form.Item>
                </Form>
            </Drawer>
        </div>
    );
};

export default Departments;
